export const LOGO_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_cattle-diet-planner/artifacts/hrtdoglr_Logo%20Sembada%20Satwa%20%28Tanpa%20Background%29.png";

export const DAYS_PER_MONTH = 30;

export const COMBORAN_INGREDIENTS = [
  { id: "konsentrat", label: "Konsentrat", pct: 0.85, color: "#A06E4D" },
  { id: "polar", label: "Polar", pct: 0.1, color: "#D38B60" },
  { id: "onggok", label: "Onggok", pct: 0.48, color: "#8A5230" },
  { id: "premix", label: "Premix", pct: 0.03, color: "#E0A988" },
  { id: "garam", label: "Garam", pct: 0.02, color: "#C99A6A" },
];

export const MIX_KEYS_BY_TYPE = {
  hijauan: ["konsentrat", "polar", "premix", "garam"],
  silase: ["konsentrat", "polar", "onggok", "premix", "garam"],
};

const INGREDIENT_META = Object.fromEntries(
  COMBORAN_INGREDIENTS.map((i) => [i.id, i])
);

// Normalize a backend model (percentages) into a fraction-based model with label
export function normalizeModel(m) {
  return {
    id: m.id,
    label: m.name ?? m.label ?? m.id,
    comboran: (m.comboran ?? 50) / 100,
    hijauan: (m.hijauan ?? 50) / 100,
    type: m.type === "silase" ? "silase" : "hijauan",
    silaseKonversi: (m.silase_konversi ?? 50) / 100,
    mix: m.mix ?? {},
    badge: "bg-[#FBEAD6] text-[#B87148] border-[#D38B60]",
  };
}

export const PRICE_ITEMS = [
  { id: "konsentrat", label: "Konsentrat" },
  { id: "polar", label: "Polar" },
  { id: "onggok", label: "Onggok" },
  { id: "premix", label: "Premix" },
  { id: "garam", label: "Garam" },
  { id: "hijauan", label: "Hijauan" },
];

export const EMPTY_PRICES = {
  konsentrat: "",
  polar: "",
  onggok: "",
  premix: "",
  garam: "",
  hijauan: "",
};

export function computeCost(result, prices) {
  if (!result) return null;
  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) || n < 0 ? 0 : n;
  };
  const items = result.ingredients.map((ing) => {
    const price = num(prices?.[ing.id]);
    return {
      id: ing.id,
      label: ing.label,
      color: ing.color,
      kg: ing.kg,
      monthlyKg: ing.monthlyKg,
      price,
      dailyCost: ing.kg * price,
      monthlyCost: ing.monthlyKg * price,
    };
  });
  if (result.model?.type === "silase") {
    const hijauanPrice = num(prices?.hijauan);
    items.push({
      id: "hijauan",
      label: "Hijauan",
      color: "#C99A6A",
      kg: result.hijauanKg,
      monthlyKg: result.monthly.hijauanKg,
      price: hijauanPrice,
      dailyCost: result.hijauanKg * hijauanPrice,
      monthlyCost: result.monthly.hijauanKg * hijauanPrice,
    });
  } else {
    const onggokPrice = num(prices?.onggok);
    items.push({
      id: "onggok",
      label: "Onggok (Hijauan)",
      color: "#8A5230",
      kg: result.hijauanKg,
      monthlyKg: result.monthly.hijauanKg,
      price: onggokPrice,
      dailyCost: result.hijauanKg * onggokPrice,
      monthlyCost: result.monthly.hijauanKg * onggokPrice,
    });
  }
  const dailyCost = items.reduce((s, i) => s + i.dailyCost, 0);
  const monthlyCost = dailyCost * DAYS_PER_MONTH;
  const hasAnyPrice = items.some((i) => i.price > 0);
  return { items, dailyCost, monthlyCost, hasAnyPrice };
}

export const fmtRupiah = (n) =>
  "Rp " +
  Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export function calculateFeed(totalWeight, model) {
  const weight = parseFloat(totalWeight);
  if (!weight || weight <= 0 || !model) return null;

  const totalFeed = weight * 0.1;
  const comboranKg = totalFeed * model.comboran;
  const hijauanKg = totalFeed * model.hijauan;
  const isSilase = model.type === "silase";
  const mixKeys = MIX_KEYS_BY_TYPE[isSilase ? "silase" : "hijauan"];
  const mixSum = mixKeys.reduce((s, k) => s + (parseFloat(model.mix?.[k]) || 0), 0);
  const scale = mixSum > 0 && Math.abs(mixSum - 100) > 0.01 ? 100 / mixSum : 1;
  const ingredients = COMBORAN_INGREDIENTS.filter((ing) => mixKeys.includes(ing.id)).map((ing) => {
    const rawPct = (model.mix && model.mix[ing.id] != null ? model.mix[ing.id] / 100 : ing.pct) * scale;
    return {
      ...ing,
      pct: rawPct,
      kg: comboranKg * rawPct,
      monthlyKg: comboranKg * rawPct * DAYS_PER_MONTH,
    };
  });
  const silaseKg = isSilase ? hijauanKg * (model.silaseKonversi ?? 0.5) : null;
  const forageKg = silaseKg ?? hijauanKg;
  const hijauanPagiKg = forageKg * 0.3;
  const hijauanSoreKg = forageKg * 0.7;

  return {
    totalWeight: weight,
    model,
    totalFeed,
    comboranKg,
    hijauanKg,
    silaseKg,
    hijauanPagiKg,
    hijauanSoreKg,
    ingredients,
    monthly: {
      totalFeed: totalFeed * DAYS_PER_MONTH,
      comboranKg: comboranKg * DAYS_PER_MONTH,
      hijauanKg: hijauanKg * DAYS_PER_MONTH,
      silaseKg: silaseKg != null ? silaseKg * DAYS_PER_MONTH : null,
    },
  };
}

export const fmt = (n) =>
  Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

export function buildShareText(c, name, cost) {
  if (!c) return "";
  const lines = [
    "RINCIAN PAKAN SAPI",
    ...(name ? [`Nama Sapi/Kandang: ${name}`] : []),
    `Model: ${c.model.label} (Comboran : ${c.model.type === "silase" ? "Silase" : "Onggok"} = ${c.model.comboran * 100}% : ${c.model.hijauan * 100}%)`,
    `Total Bobot Sapi: ${fmt(c.totalWeight)} kg`,
    `Kebutuhan Pakan (10% bobot): ${fmt(c.totalFeed)} kg/hari`,
    `- Comboran: ${fmt(c.comboranKg)} kg/hari`,
    ...(c.silaseKg != null
      ? [
          `- Hijauan Segar: ${fmt(c.hijauanKg)} kg/hari`,
          `- Silase (konversi ${c.hijauanKg ? Math.round((c.silaseKg / c.hijauanKg) * 100) : 50}%): ${fmt(c.silaseKg)} kg/hari`,
        ]
      : [`- Onggok (Hijauan): ${fmt(c.hijauanKg)} kg/hari`]),
    `  · Pagi (30%): ${fmt(c.hijauanPagiKg ?? c.hijauanKg * 0.3)} kg`,
    `  · Sore (70%): ${fmt(c.hijauanSoreKg ?? c.hijauanKg * 0.7)} kg`,
    ...(c.silaseKg != null
      ? [`Catatan: Polar fermentasi ${fmt(c.hijauanKg * 0.03)}-${fmt(c.hijauanKg * 0.05)} kg (3-5% dari hijauan segar)`]
      : []),
    "",
    "Rincian Comboran (per hari):",
    ...c.ingredients.map(
      (i) => `- ${i.label} (${i.pct * 100}%): ${fmt(i.kg)} kg`
    ),
    "",
    "Estimasi Kebutuhan Bulanan (30 hari):",
    `- Total Pakan: ${fmt(c.monthly.totalFeed)} kg`,
    `- Comboran: ${fmt(c.monthly.comboranKg)} kg`,
    `- ${c.silaseKg != null ? "Hijauan Segar" : "Onggok (Hijauan)"}: ${fmt(c.monthly.hijauanKg)} kg`,
    ...(c.monthly.silaseKg != null ? [`- Silase: ${fmt(c.monthly.silaseKg)} kg`] : []),
    ...c.ingredients.map(
      (i) => `- ${i.label}: ${fmt(i.monthlyKg)} kg`
    ),
    ...(cost && cost.hasAnyPrice
      ? [
          "",
          "Estimasi Biaya:",
          `- Per Hari: ${fmtRupiah(cost.dailyCost)}`,
          `- Per Bulan: ${fmtRupiah(cost.monthlyCost)}`,
        ]
      : []),
  ];
  return lines.join("\n");
}
